import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { isSupabaseConfigured, supabase } from "@/services/supabase-client.service";
import {
    getActiveWaitingSessions,
    getWaitingZones,
    callPatient,
    startTreatment,
    completeTreatment,
    markNoShow,
    cancelWaitingSession,
    updatePriority,
    changeZone,
    assignRoom,
    checkInPatient,
    updateSessionNotes,
    type CheckInInput,
} from "@/services/waiting-room.service";
import { useToast } from "@/components/shared/Toast";

export const waitingRoomKeys = {
    all: ["waitingRoom"] as const,
    sessions: (practiceId?: string) =>
        [...waitingRoomKeys.all, "sessions", practiceId].filter(Boolean),
    zones: (practiceId?: string) => [...waitingRoomKeys.all, "zones", practiceId].filter(Boolean),
};

export function useWaitingRoomRealtime(practiceId?: string) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!practiceId || !isSupabaseConfigured()) return;

        const channel = supabase
            .channel(`waitingRoom-realtime-${practiceId}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "practice_waiting_sessions",
                    filter: `practice_id=eq.${practiceId}`,
                },
                () => {
                    queryClient.invalidateQueries({ queryKey: waitingRoomKeys.all });
                },
            )
            .subscribe();

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [practiceId, queryClient]);
}

export function useWaitingSessionsQuery(practiceId?: string) {
    return useQuery({
        queryKey: waitingRoomKeys.sessions(practiceId),
        queryFn: () => getActiveWaitingSessions(practiceId!),
        enabled: !!practiceId,
    });
}

export function useWaitingZonesQuery(practiceId?: string) {
    return useQuery({
        queryKey: waitingRoomKeys.zones(practiceId),
        queryFn: () => getWaitingZones(practiceId!),
        enabled: !!practiceId,
    });
}

export function useCheckInPatientMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: (input: CheckInInput) => checkInPatient(input),
        onSuccess: () => {
            addToast({ type: "success", title: "Patient eingecheckt" });
            queryClient.invalidateQueries({ queryKey: waitingRoomKeys.sessions(practiceId) });
        },
        onError: () => {
            addToast({ type: "error", title: "Fehler beim Einchecken" });
        },
    });
}

export function useCallPatientMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: ({
            sessionId,
            doctorId,
            room,
        }: {
            sessionId: string;
            doctorId?: string;
            room?: string;
        }) => callPatient(sessionId, doctorId, room),
        onSuccess: () => {
            addToast({ type: "success", title: "Patient aufgerufen" });
            queryClient.invalidateQueries({ queryKey: waitingRoomKeys.sessions(practiceId) });
        },
        onError: () => {
            addToast({ type: "error", title: "Fehler beim Aufrufen" });
        },
    });
}

export function useStartTreatmentMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (sessionId: string) => startTreatment(sessionId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: waitingRoomKeys.sessions(practiceId) });
        },
    });
}

export function useCompleteTreatmentMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sessionId, notes }: { sessionId: string; notes?: string }) =>
            completeTreatment(sessionId, notes),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: waitingRoomKeys.sessions(practiceId) });
        },
    });
}

export function useMarkNoShowMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (sessionId: string) => markNoShow(sessionId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: waitingRoomKeys.sessions(practiceId) });
        },
    });
}

export function useCancelWaitingSessionMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: (sessionId: string) => cancelWaitingSession(sessionId),
        onSuccess: () => {
            addToast({ type: "success", title: "Sitzung storniert" });
            queryClient.invalidateQueries({ queryKey: waitingRoomKeys.sessions(practiceId) });
        },
        onError: () => {
            addToast({ type: "error", title: "Fehler beim Stornieren" });
        },
    });
}

export function useUpdatePriorityMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sessionId, priority }: { sessionId: string; priority: 0 | 1 | 2 }) =>
            updatePriority(sessionId, priority),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: waitingRoomKeys.sessions(practiceId) });
        },
    });
}

export function useChangeZoneMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sessionId, zoneId }: { sessionId: string; zoneId: string }) =>
            changeZone(sessionId, zoneId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: waitingRoomKeys.sessions(practiceId) });
        },
    });
}

export function useAssignRoomMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sessionId, room }: { sessionId: string; room: string }) =>
            assignRoom(sessionId, room),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: waitingRoomKeys.sessions(practiceId) });
        },
    });
}

export function useUpdateSessionNotesMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sessionId, notes }: { sessionId: string; notes: string }) =>
            updateSessionNotes(sessionId, notes),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: waitingRoomKeys.sessions(practiceId) });
        },
    });
}
